import React from 'react';
import { CheckCircle, Download } from 'lucide-react';
import axios from 'axios';

const ResultsSection = ({ result, resetApp }) => {
    const handleDownload = async (videoId) => {
        try {
            const response = await axios.get(`/api/download/${videoId}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `worm_analysis_${videoId}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Download failed: ' + (err.response?.data?.detail || err.message));
        }
    };

    if (!result) return null;

    return (
        <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="text-green-600" size={32} />
                <h2 className="text-2xl font-bold text-gray-800">Analysis Complete</h2>
            </div>

            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-8 mb-6 text-white text-center">
                <p className="text-lg mb-2 opacity-90">Total Worms Detected</p>
                <p className="text-6xl font-bold">{result.totalWorms}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard
                    label="Max in Single Frame"
                    value={result.maxInFrame}
                    color="blue"
                />
                <StatCard
                    label="Avg per Frame"
                    value={result.avgPerFrame}
                    color="green"
                />
                <StatCard
                    label="Frames Analyzed"
                    value={result.framesProcessed}
                    color="purple"
                />
                <StatCard
                    label="Processing Time"
                    value={result.processingTime}
                    color="yellow"
                />
                <StatCard
                    label="Confidence"
                    value={result.confidence}
                    color="indigo"
                />
            </div>

            <div className="mt-6 flex gap-4">
                <button
                    onClick={() => handleDownload(result.videoId)}
                    className="flex items-center justify-center gap-2 flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                    <Download size={20} />
                    Download Processed Video
                </button>
                <button
                    onClick={resetApp}
                    className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                >
                    Analyze Another Video
                </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 text-center">
                    The downloaded video includes visual annotations showing detected worms with bounding boxes and counters.
                </p>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, color }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-green-50 text-green-600',
        purple: 'bg-purple-50 text-purple-600',
        yellow: 'bg-yellow-50 text-yellow-600',
        indigo: 'bg-indigo-50 text-indigo-600'
    };

    return (
        <div className={`${colorClasses[color]} p-4 rounded-lg`}>
            <p className="text-sm text-gray-600 mb-1">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
        </div>
    );
};

export default ResultsSection;